import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * usePushSubscription — manages browser push subscription for the
 * current user. Three external touchpoints:
 *
 *   - SettingsModal "push notifications" toggle: when flipped ON we
 *     request browser permission, register the SW, subscribe, and
 *     write the subscription doc. When flipped OFF we unsubscribe
 *     and remove the doc.
 *
 *   - Service worker (`/reminder-sw.js`) — handles the push events
 *     and surfaces the OS notification.
 *
 *   - Cloud Function `firePendingReminders` — reads the subscription
 *     doc and dispatches encrypted pushes via web-push.
 *
 * Storage: users/{uid}/pushSubscriptions/{deviceId}. Each browser
 * gets its own doc keyed by a deviceId persisted in localStorage so
 * users can opt different devices in/out independently.
 *
 * VAPID public key — the public half of an asymmetric keypair, safe
 * to commit (the matching PRIVATE key lives in Firebase secrets and
 * is what actually authorises pushes). Walter 2026-05-07: previously
 * we read this only from REACT_APP_VAPID_PUBLIC_KEY, but worktree
 * builds silently lost it because .env.production is gitignored,
 * which left production users seeing "Push notifications are not
 * configured (missing VAPID key)" after a deploy. Hardcoding the
 * literal as a fallback fixes that class of bug for good. The env
 * var still wins if set, so dev/staging keys remain swappable.
 */
const VAPID_PUBLIC_KEY_FALLBACK = 'BF3CnLxR1mYfNE4ca-t6A4BtHL35fY7rsprSxkjk6IZC4m_rBj9xGOqa0qwTJUS1-C0l9RdJSSxeJB46i_kl6dM';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY_FALLBACK;
const DEVICE_ID_KEY = 'lists_push_device_id';
const SUPPORTS_PUSH =
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `temp-${Math.random().toString(36).slice(2, 10)}`;
  }
}

// VAPID keys come over the wire as URL-safe base64. PushManager wants
// a Uint8Array. Standard transform.
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushSubscription(userId) {
  // 'unknown' | 'denied' | 'granted' | 'default' | 'unsupported'
  const [permission, setPermission] = useState('unknown');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    if (!SUPPORTS_PUSH) {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission);
  }, []);

  // Detect whether THIS browser already has a live subscription. We
  // don't infer this from Firestore (the doc could be stale after the
  // browser revoked permission) — read directly from the SW.
  useEffect(() => {
    if (!SUPPORTS_PUSH) return;
    if (!userId) { setSubscribed(false); return; }
    let cancelled = false;
    navigator.serviceWorker.getRegistration('/reminder-sw.js').then(reg => {
      if (cancelled) return;
      if (!reg) { setSubscribed(false); return; }
      reg.pushManager.getSubscription().then(sub => {
        if (cancelled) return;
        setSubscribed(!!sub);
      }).catch(() => setSubscribed(false));
    }).catch(() => setSubscribed(false));
    return () => { cancelled = true; };
  }, [userId]);

  const subscribe = useCallback(async () => {
    setError(null);
    if (!SUPPORTS_PUSH) {
      setError('This browser does not support push notifications.');
      return false;
    }
    if (!VAPID_PUBLIC_KEY) {
      setError('Push notifications are not configured (missing VAPID key).');
      return false;
    }
    if (!userId) {
      setError('Sign in to enable push notifications.');
      return false;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setBusy(false);
        return false;
      }

      // Register the worker (idempotent — if already registered with
      // the same script URL the browser reuses).
      const reg = await navigator.serviceWorker.register('/reminder-sw.js');
      // Wait for it to be active before subscribing.
      if (reg.installing || reg.waiting) {
        await new Promise(resolve => {
          const worker = reg.installing || reg.waiting;
          if (!worker) { resolve(); return; }
          if (worker.state === 'activated') { resolve(); return; }
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') resolve();
          });
          // Fallback timeout — don't hang forever.
          setTimeout(resolve, 4000);
        });
      }

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      const deviceId = getOrCreateDeviceId();
      const subJSON = sub.toJSON();
      await setDoc(doc(db, 'users', userId, 'pushSubscriptions', deviceId), {
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
        userAgent: navigator.userAgent || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setSubscribed(true);
      setBusy(false);
      return true;
    } catch (e) {
      console.error('[usePushSubscription] subscribe failed', e);
      // Walter 2026-05-07: pushManager.subscribe surfaces opaque
      // browser-level errors like "Registration failed - push service
      // error" when the OS or browser is blocking pushes for reasons
      // outside our control (Windows notifications off, browser
      // notifications off, corporate firewall blocking FCM, etc.).
      // Translate the most common ones into actionable messages so
      // users know to fix their environment, not the app.
      const raw = (e?.message || '').toLowerCase();
      let friendly = e?.message || 'Failed to enable push notifications.';
      if (raw.includes('registration failed') || raw.includes('push service')) {
        friendly = 'Your browser couldn’t register for push. This usually means notifications are turned off at the OS level (Windows: Settings → System → Notifications) or the browser is blocking them (chrome://settings/content/notifications). Email reminders still work on every device.';
      } else if (raw.includes('permission') || raw.includes('denied')) {
        friendly = 'Push permission was denied. Re-enable it in your browser’s site settings (lock icon next to the URL → Notifications: Allow), then try again. Email reminders still work in the meantime.';
      } else if (raw.includes('not supported')) {
        friendly = 'This browser doesn’t support push notifications. Try Chrome, Edge, or Firefox. Email reminders still work everywhere.';
      }
      setError(friendly);
      setBusy(false);
      return false;
    }
  }, [userId]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    if (!SUPPORTS_PUSH) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/reminder-sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          try { await sub.unsubscribe(); } catch (e) { /* fall through */ }
        }
      }
      if (userId) {
        const deviceId = getOrCreateDeviceId();
        try {
          await deleteDoc(doc(db, 'users', userId, 'pushSubscriptions', deviceId));
        } catch (e) { console.warn('[usePushSubscription] cleanup failed', e); }
      }
      setSubscribed(false);
    } catch (e) {
      console.error('[usePushSubscription] unsubscribe failed', e);
      setError(e?.message || 'Failed to disable push notifications.');
    }
    setBusy(false);
  }, [userId]);

  // Keep lastUserIdRef in sync — useful if we ever want to react to
  // user switches (anonymous → signed in) by re-subscribing under
  // the new uid.
  useEffect(() => { lastUserIdRef.current = userId; }, [userId]);

  return {
    permission,
    subscribed,
    busy,
    error,
    supported: SUPPORTS_PUSH,
    subscribe,
    unsubscribe,
  };
}
